<template>
  <div class="fms-dashboard">
    <div class="px-2">
      <data-filter :onApplyFilter="onApplyFilter" :hideDate="true" :hideRangeType="true" :hideInterval="true" />
    </div>

    <div class="dashboard-card">
      <template v-if="isLoading">
        <div class="text-center mt-5">
          <b-spinner label="Spinning"></b-spinner>
        </div>
      </template>

      <template v-else-if="currAssetSummary && !isLoading">
        <!-- Data percentage -->
        <div style="padding: 0 10px;">
          <!-- <data-percentage /> -->
        </div>
        <!-- ./ Data percentage -->

        <fms-header :currAsset="currAsset" :currAssetLastData="currAssetLastData"
          :isRealtimeConnected="isRealtimeConnected" :rangeLabel="rangeLabel" :deviceStatus="currAssetDeviceStatus" />

        <!-- <pre>{{ currAssetSummary }}</pre> -->

        <b-row class="px-2 align-stretch row-tight section-gap section-gap--first">
          <b-col lg="8" class="mb-2 mb-lg-0 d-flex">
            <today-cruise-snapshot :currAsset="currAsset" :currAssetLastData="currAssetLastData"
              :currAssetSummary="currAssetSummary" @detail="openAssetDetailModal" :deviceStatus="currAssetDeviceStatus" />
          </b-col>
          <b-col lg="4" class="d-flex">
            <fuel-consumption-card :currAsset="currAsset" :currAssetSummary="currAssetSummary"
              :avgCombinedFlow="avgCombinedFlow" :totalAeCons="totalAeCons" :totalEngineCons="totalEngineCons"
              :isSingleMainEngine="isSingleMainEngine" :deviceStatus="currAssetDeviceStatus" />
          </b-col>
        </b-row>
        <!-- ./ Row one -->

        <b-row class="px-2 align-stretch row-tight section-gap">
          <b-col lg="8" class="mb-2 mb-lg-0 d-flex">
            <fms-map-section :currAsset="currAsset" :center="center" :zoom="zoom"
              :trail-range="mapTrailRange" />
          </b-col>
          <b-col lg="4" class="d-flex">
            <navigation-sea-conditions :currAssetLastData="currAssetLastData" />
          </b-col>
        </b-row>

        <div class="section-gap">
          <flow-rate-trend-card
            :currAssetSummary="currAssetSummary"
            :currAssetLastData="currAssetLastData"
            :isSingleMainEngine="isSingleMainEngine"
            :deviceStatus="currAssetDeviceStatus"
          />
        </div>

        <div class="section-gap">
          <engine-summary-section :currAsset="currAsset" :currAssetSummary="currAssetSummary"
            :currAssetLastData="currAssetLastData" :isSingleMainEngine="isSingleMainEngine" :deviceStatus="currAssetDeviceStatus" />
        </div>

        <div class="section-gap">
          <rpm-fuel-speed-card :currAssetSummary="currAssetSummary" :isSingleMainEngine="isSingleMainEngine" :deviceStatus="currAssetDeviceStatus" />
        </div>
      </template>

      <template v-else-if="!currAssetSummary">
        <div class="text-center mt-5">
          <h5>No Data</h5>
        </div>
      </template>
    </div>

    <asset-detail-modal v-model="displayAssetDetail" :detailAsset="detailAsset" :detailDevices="detailDevices" />
  </div>
</template>

<script>
import moment from 'moment';
import { mapState, mapActions } from 'vuex';
import { formatNum, parseNum } from '@/util/number';
import { DISPLAY_DATETIME, CHART_TOOLTIP } from '@/util/apiDatetime';
import { buildFmsSummary } from '../../util/fms-summary';
import { initAssetSummary, applyAssetSummaryFilter } from '../../util/asset-summary-loader';
import { formatDateTime } from '../../util/date';

import DeviceSvc from '../../services/DeviceSvc';

import cruiseDistanceIcon from '../../assets/images/dashboard/cruise-distance.svg';
import cruiseLocationIcon from '../../assets/images/dashboard/cruise-location.svg';
import cruiseSpeedIcon from '../../assets/images/dashboard/cruise-speed.svg';

import DataFilter from '../../components/DataFilter.vue';
// import TodayCruiseSnapshot from '../../components/fms/TodayCruiseSnapshot.vue';
import TodayCruiseSnapshot from '../../components/fms/TodayCruiseSnapshot.vue';
import FmsHeader from '../../components/fms/FmsHeader.vue';
import FuelConsumptionCard from '../../components/fms/FuelConsumptionCard.vue';
import EngineSummarySection from '../../components/fms/EngineSummarySection.vue';
import FmsMapSection from '../../components/fms/FmsMapSection.vue';
import RpmFuelSpeedCard from '../../components/fms/RpmFuelSpeedCard.vue';
import AssetDetailModal from '../../components/fms/AssetDetailModal.vue';
import NavigationSeaConditions from '../../components/fms/NavigationSeaConditions.vue';
import FlowRateTrendCard from '../../components/fms/FlowRateTrendCard.vue';

export default {
  name: 'FmsDashboard',
  components: {
    DataFilter,
    FmsHeader,
    TodayCruiseSnapshot,
    FuelConsumptionCard,
    EngineSummarySection,
    FmsMapSection,
    RpmFuelSpeedCard,
    AssetDetailModal,
    NavigationSeaConditions,
    FlowRateTrendCard,
  },
  data() {
    return {
      // eslint-disable-next-line no-undef
      center: L.LatLng(0, 0),
      zoom: 10,
      img: {
        cruiseDistanceIcon,
        cruiseLocationIcon,
        cruiseSpeedIcon,
      },
      masks: {
        input: 'DD-MM-YYYY HH:mm',
      },
      isLoading: true,
      displayAssetDetail: false,
      detailAsset: {},
      detailDevices: [],
      appliedMassId: null,
      appliedDateRange: null,
      appliedInterval: null,
    };
  },
  async mounted() {
    const date = this.getLast24HoursRange();

    const initResult = await initAssetSummary(this.$store, {
      dateRange: date,
      anchorHoursFromLastData: 24,
      setFilterDate: false,
      allowMinute: false,
      withDevices: true,
      withLastData: true,
      summarySource: 'rollup',
      // Tiga kartu baca .series (FuelConsumptionCard, RpmFuelSpeedCard,
      // FlowRateTrendCard), jadi deret waktunya wajib ikut.
      withSeries: true,
    });

    this.appliedMassId = initResult.currentMassId;
    this.appliedDateRange = initResult.dateRange;
    this.appliedInterval = initResult.interval;

    setInterval(async () => {
      const now = new Date();
      const nextMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0, 0);
      const diffTime = Math.abs(nextMidnight - now);
      if (diffTime < 1000) {
        await this.$store.dispatch('asset/getLastData');
      }
    }, 1000 * 60);

    this.isLoading = false;
  },
  computed: {
    currAsset() {
      const currMassId = this.appliedMassId || this.$store.getters['filter/currentMassId'];
      const assets = this.$store.getters['asset/assets'];
      if (!assets) return null;
      const asset = assets.find((asset) => asset.massId === currMassId);
      // console.log('[FMSDashboard] currAsset:', asset);
      return asset;
    },
    isSingleMainEngine() {
      // Single main engine = aset hanya punya 1 tipe RPM (diturunkan dari data).
      return this.currAsset?.deviceStatus?.rpmTypes?.length === 1;
    },
    currAssetDeviceStatus() {
      return this.currAsset?.deviceStatus || {};
    },
    currAssetLastData() {
      if (!this.currAsset) return null;
      const {
        lastGpsData,
        lastGpsDataPulse,
        lastRpmData,
        lastAeData,
        lastFmData,
        lastTemperatureData,
        lastPowermeterData,
      } = this.currAsset;
      const gpsData = lastGpsDataPulse || lastGpsData;

      const mappedData = {
        gps: {
          ...(gpsData || {}),
          timestamp: gpsData?.timestamp ? formatDateTime(gpsData.timestamp * 1000, DISPLAY_DATETIME) : '-',
        } || null,
        rpm: {
        },
        fm: {},
        temp: {
          port: { exhaust: null, reservoir: null },
          starboard: { exhaust: null, reservoir: null },
          main: { exhaust: null, reservoir: null },
        },
        ae: {},
      };

      const rpmTypes = this.currAssetDeviceStatus?.rpmTypes || [];

      rpmTypes.forEach((rpmType) => {
        const rpmData = lastRpmData?.find((data) => data.type === rpmType);
        mappedData.rpm[rpmType] = rpmData ? {
          rpm: rpmData.rpm,
          timestamp: formatDateTime(rpmData.timestamp * 1000, DISPLAY_DATETIME),
        } : null;
      });

      if (lastFmData) {
        lastFmData.forEach((data) => {
          const key = data.device?.devcLabel; // e.g. PORT_IN, CENTER_OUT
          if (key) {
            mappedData.fm[key] = {
              flowrate: data.volumeFlowrate,
              density: data.density,
              temp: data.temperature,
              timestamp: formatDateTime(data.timestamp * 1000, CHART_TOOLTIP),
            };
          }
        });
      }

      const powermeterDataByAeNumber = {};
      if (lastPowermeterData) {
        lastPowermeterData.forEach((pmData) => {
          const aeNumberMatch = pmData.device?.devcLabel?.match(/_(\d+)$/);
          if (aeNumberMatch) {
            const aeNum = parseInt(aeNumberMatch[1], 10);
            powermeterDataByAeNumber[aeNum] = pmData;
          }
        });
      }

      if (lastAeData) {
        lastAeData.forEach((aeData) => {
          const aeNum = aeData.no + 1;
          const pmData = powermeterDataByAeNumber[aeNum] || {};

          mappedData.ae[aeNum] = {
            rpm: aeData.rpm,
            totalPower: pmData.activePower,
            apparentPower: pmData.apparentPower,
            totalEnergy: pmData.totalEnergy,
            frequency: pmData.frequency,
            voltageLtoL: pmData.voltageLtoL,
            voltageLtoN: pmData.voltageLtoN,
            current: pmData.current,
            powerFactor: pmData.powerFactor,
            runningSeconds: pmData.runningSeconds,
          };
        });
      }

      (lastTemperatureData || []).forEach((data) => {
        const type = data.type;
        const label = data.device?.devcLabel;
        const tempValue = data.temperature;
        const timestamp = formatDateTime(data.timestamp * 1000, CHART_TOOLTIP);

        if (type === 'PORT') {
          if (label === 'EXHAUST_TEMPERATURE') {
            mappedData.temp.port.exhaust = tempValue;
            mappedData.temp.port.timestampExhaust = timestamp;
          } else if (label === 'TEMPERATURE') {
            mappedData.temp.port.reservoir = tempValue;
            mappedData.temp.port.timestampReservoir = timestamp;
          }
        } else if (type === 'STARBOARD') {
          if (label === 'EXHAUST_TEMPERATURE') {
            mappedData.temp.starboard.exhaust = tempValue;
            mappedData.temp.starboard.timestampExhaust = timestamp;
          } else if (label === 'TEMPERATURE') {
            mappedData.temp.starboard.reservoir = tempValue;
            mappedData.temp.starboard.timestampReservoir = timestamp;
          }
        } else if (type === 'MAINENGINE' || type === 'MAIN' || type === 'CENTER') {
          if (label === 'EXHAUST_TEMPERATURE') {
            mappedData.temp.main.exhaust = tempValue;
            mappedData.temp.main.timestampExhaust = timestamp;
          } else if (label === 'TEMPERATURE') {
            mappedData.temp.main.reservoir = tempValue;
            mappedData.temp.main.timestampReservoir = timestamp;
          }
        }
      });

      return mappedData;
    },
    currAssetSummary() {
      const builtSummary = buildFmsSummary({
        deviceStatus: this.currAssetDeviceStatus,
        summary: this.currAsset?.summary,
        summarySource: this.currAsset?.summarySource,
        dateRange: this.appliedDateRange || this.dateRange,
        interval: this.appliedInterval || this.interval,
      });
      return builtSummary;
    },
    totalAeCons() {
      // Total konsumsi semua AE sudah dijumlah di buildFmsSummary (ikut aeCount),
      // jadi tinggal pakai itu — tanpa hardcode ae1/ae2/ae3.
      if (!this.currAssetDeviceStatus?.aeCount) return '-';
      const total = parseNum(this.currAssetSummary?.ae?.fuelConsumption);
      return Number.isFinite(total) ? formatNum(total, 2) : '-';
    },
    totalEngineCons() {
      if (!this.currAssetSummary) return '-';
      const meRaw = parseNum(this.currAssetSummary.fm?.meEngineCons);
      const me = Number.isFinite(meRaw) ? meRaw : null;
      const aeRaw = parseNum(this.totalAeCons);
      const ae = Number.isFinite(aeRaw) ? aeRaw : null;
      if (me === null && ae === null) return '-';
      const total = (me ?? 0) + (ae ?? 0);
      return formatNum(total, 2);
    },
    avgCombinedFlow() {
      if (!this.currAssetSummary?.fm) return '-';
      const { fm } = this.currAssetSummary;
      let totalFlow = 0;
      let count = 0;

      if (fm.PORT?.in?.avgFuelFlow) {
        totalFlow += parseNum(fm.PORT.in.avgFuelFlow) || 0;
        count++;
      }
      if (fm.STARBOARD?.in?.avgFuelFlow) {
        totalFlow += parseNum(fm.STARBOARD.in.avgFuelFlow) || 0;
        count++;
      }
      if (fm.CENTER?.in?.avgFuelFlow) {
        totalFlow += parseNum(fm.CENTER.in.avgFuelFlow) || 0;
        count++;
      }

      if (count === 0) return '0.00';
      return formatNum((totalFlow / count), 2);
    },
    lastDataAgeMinutes() {
      const lastTimestamp = this.currAsset?.lastGpsDataPulse?.timestamp || this.currAsset?.lastGpsData?.timestamp;
      if (!lastTimestamp) return null;
      return moment().diff(moment.unix(lastTimestamp), 'minutes', true);
    },
    isRealtimeConnected() {
      if (this.lastDataAgeMinutes === null) return false;
      return this.lastDataAgeMinutes <= 10;
    },
    dateRange: {
      get() {
        const { start, end } = this.$store.getters['filter/date'];
        const startDate = new Date(moment(start).valueOf());
        const endDate = new Date(moment(end).valueOf());

        return { start: startDate, end: endDate };
      },
      set(val) {
        const start = moment(val.start);
        const end = moment(val.end);

        this.$store.commit('filter/SET_DATE', { start, end });
      },
    },
    mapTrailRange() {
      const r = this.appliedDateRange || this.dateRange;
      if (!r?.start || !r?.end) return undefined;
      return { start: moment(r.start).unix(), end: moment(r.end).unix() };
    },
    rangeLabel() {
      const { start, end } = this.appliedDateRange || this.dateRange;
      const startLabel = formatDateTime(start, CHART_TOOLTIP);
      const endLabel = formatDateTime(end, CHART_TOOLTIP);
      return `${startLabel} - ${endLabel}`;
    },
    ...mapState('asset', ['assets']),
    ...mapState('filter', {
      currentMassId: (state) => state.currentMassId,
      interval: (state) => state.interval,
    }),
    selectedInterval: {
      get() {
        return this.interval;
      },
      set(val) {
        this.$store.dispatch('filter/setInterval', val);
      },
    },
    selectedAsset: {
      get() {
        return this.currentMassId;
      },
      set(val) {
        this.$store.dispatch('filter/setCurrentMassId', val);
      },
    },
  },
  methods: {
    ...mapActions(['updatekapalTrail']),
    zoomToShip(ship) {
      // eslint-disable-next-line no-undef
      this.center = L.LatLng(ship.position[0], ship.position[1]);
      this.zoom = 15;
    },
    async onApplyFilter() {
      this.isLoading = true;
      try {
        const date = this.getLast24HoursRange();
        const result = await applyAssetSummaryFilter(this.$store, {
          dateRange: date,
          anchorHoursFromLastData: 24,
          setFilterDate: false,
          interval: this.selectedInterval || this.interval,
          massId: this.selectedAsset || this.currentMassId,
          summarySource: 'rollup',
          withSeries: true,
        });
        this.appliedMassId = result.massId;
        this.appliedDateRange = result.dateRange;
        this.appliedInterval = result.interval;
      } catch (err) {
        console.error('Apply Filter Error', err);
      } finally {
        this.isLoading = false;
      }
    },
    getLast24HoursRange() {
      const end = moment().startOf('hour');
      const start = end.clone().subtract(24, 'hours');
      return { start, end };
    },
    async openAssetDetailModal(asset) {
      if (!asset) return;
      this.detailAsset = asset;
      this.detailDevices = [];
      try {
        const param = {
          findField: 'devcMassId',
          findValue: asset.massId,
          max: 100,
        };
        const devices = await DeviceSvc.getDevicesByAssetId(param);
        if (devices && devices.data && devices.data.data) {
          this.detailDevices = devices.data.data;
        }
      } catch (e) {
        console.error('Failed to fetch devices', e);
      }
      this.displayAssetDetail = true;
    },
  },
  watch: {
    // currAsset(newAsset) {
    //   console.log('[FMS] Asset:', newAsset);
    // },
  },
};
</script>

<style scoped>
.fms-dashboard {
  width: 100%;
  margin: 0 auto;
  background: #f5f7fb;
  padding-bottom: 30px;
}

.dashboard-card {
  margin: 0px 8px 0px 8px;
  padding: 0 0 20px 0;
  background: transparent;
}

.dashboard-card ::v-deep .iq-card {
  background: #ffffff;
  border: 2px solid #E8ECF3;
  border-radius: 20px;
  box-shadow: none;
}

.row-tight {
  margin-left: -8px;
  margin-right: -8px;
}

.row-tight>[class*='col-'] {
  padding-left: 8px;
  padding-right: 8px;
}

.section-gap {
  margin-top: 12px;
}

.section-gap--first {
  margin-top: 0;
}
</style>
